/**
 * Apache ECharts shared theme for Fix indicators.
 *
 * Single source of truth for colors, typography, borders, and animations
 * across all chart components. All chart configs should import from here.
 */

// ── Brand palette ────────────────────────────────────────────────────────
export const colors = {
  // Primary brand
  primary: '#0F3565', // Navy (main bars/lines)
  primaryLight: '#3C8EA6', // Teal accent
  primaryDark: '#212950', // Deep navy

  // Accent / success
  accent: '#BBCF20', // Lime green (executed/goals)
  success: '#67C20C', // Bright green (on time)

  // Warning / attention
  warning: '#DEDE21', // Yellow
  warningSoft: '#FFB300', // Orange

  // Danger
  danger: '#ED8990', // Pink/red

  // Goal line
  goal: 'rgba(255,128,0,0.6)',
  goalArea: 'rgba(255,128,0,0.08)',

  // Neutrals
  neutral: '#7A8783',
  neutralLight: '#C4E8F2',
  text: '#212950',
  textSoft: '#5A6678',
  background: '#FFFFFF',
  border: '#E0E5E9',
}

// Categorical palette for pies/multi-series charts.
export const palette = [
  '#0F3565',
  '#BBCF20',
  '#3C8EA6',
  '#a4d233',
  '#8986ca',
  '#3e6991',
  '#49c5b1',
  '#ED8990',
  '#414647',
  '#38B890',
  '#CEE6D7',
  '#7A8783',
  '#4c6d97',
  '#C4E8F2',
  '#67C20C',
  '#DEDE21',
]

/**
 * Original per-bar palette used by the legacy Chart.js components for
 * single-series bar charts (1.1, 1.3, 1.17, 1.19, 1.20). Preserved verbatim
 * so the migrated ECharts versions look identical to the originals.
 */
export const variedBarColors = [
  '#426483',
  '#BBCF20',
  '#0F3565',
  '#212950',
  '#C4E8F2',
  '#ED8990',
  '#FDB578',
  '#7EE882',
  '#8986ca',
  '#a4d233',
  '#3C8EA6',
  '#49c5b1',
  '#3e6991',
  '#38B890',
]

// Color mapping for known service order types (preserves legacy colors).
export const activityColors = {
  Calibração: '#8986ca',
  Preventiva: '#a4d233',
  Treinamento: '#3C8EA6',
  'Segurança elétrica': '#49c5b1',
  Instalação: '#414647',
  'Rotina de inspeção': '#38B890',
  'Rotina de Inspeção': '#38B890',
  Corretiva: '#3e6991',
  'Validação/Qualificação': '#CEE6D7',
  'Serviço Administrativo': '#7A8783',
  'Serviços Administrativos': '#7A8783',
  'Serviço Operacional': '#D68945',
}

// ── Typography ───────────────────────────────────────────────────────────
export const typography = {
  fontFamily: '"Roboto", "Segoe UI", sans-serif',
  fontSize: 12,
  titleSize: 14,
  legendSize: 12,
}

// ── Animation ────────────────────────────────────────────────────────────
export const animation = {
  duration: 600,
  easing: 'cubicOut',
}

// ── Common option fragments ──────────────────────────────────────────────

/** Tooltip with rounded borders + soft shadow. */
export const baseTooltip = {
  trigger: 'item',
  backgroundColor: '#FFFFFF',
  borderColor: colors.border,
  borderWidth: 1,
  borderRadius: 8,
  padding: [8, 12],
  textStyle: {
    color: colors.text,
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
  },
  extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08);',
}

export const baseAxisTooltip = {
  ...baseTooltip,
  trigger: 'axis',
  axisPointer: {
    type: 'shadow',
    shadowStyle: { color: 'rgba(15,53,101,0.04)' },
  },
}

/** Standard legend with bottom alignment + padding. */
export const baseLegend = {
  bottom: 0,
  left: 'center',
  itemWidth: 12,
  itemHeight: 12,
  itemGap: 16,
  textStyle: {
    color: colors.textSoft,
    fontFamily: typography.fontFamily,
    fontSize: typography.legendSize,
  },
  icon: 'roundRect',
}

/** Standard grid with breathing room. */
export const baseGrid = {
  left: 16,
  right: 16,
  top: 32,
  bottom: 56,
  containLabel: true,
}

/** Common axis styling. */
export const baseAxis = {
  axisLine: { lineStyle: { color: colors.border } },
  axisTick: { show: false },
  axisLabel: {
    color: colors.textSoft,
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
  },
  splitLine: { lineStyle: { color: colors.border, type: 'dashed' } },
}

/** Loading config for ECharts. */
export const loadingConfig = {
  text: 'Carregando…',
  color: colors.primary,
  textColor: colors.textSoft,
  maskColor: 'rgba(255, 255, 255, 0.85)',
  zlevel: 0,
  fontSize: 13,
  showSpinner: true,
  spinnerRadius: 12,
  lineWidth: 3,
  fontFamily: typography.fontFamily,
}
